'use client';

import { Button, Icon, Typography } from '@polyseme/design-system';
import { cn } from '@polyseme/utils/cn/cn-utils';
import { useTranslations } from 'next-intl';
import Image from 'next/image';
import { useState } from 'react';

import { images } from '~/landing/assets';

const useImageUpload = () => {
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);

  const processFile = (file?: File) => {
    if (!file?.type.startsWith('image/')) return;

    const reader = new FileReader();
    reader.onloadend = () => setImagePreview(reader.result as string);
    reader.readAsDataURL(file);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
    processFile(e.dataTransfer.files[0]);
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => setIsDragging(false);

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    processFile(e.target.files?.[0]);
  };

  return {
    imagePreview,
    isDragging,
    handleDrop,
    handleDragOver,
    handleDragLeave,
    handleFileInput,
  };
};

export const Banner = () => {
  const t = useTranslations('landing.germen');
  const {
    imagePreview,
    isDragging,
    handleDrop,
    handleDragOver,
    handleDragLeave,
    handleFileInput,
  } = useImageUpload();

  return (
    <div className="relative inset-x-1/2 mx-[calc(-50vw)] w-screen">
      <div
        className={cn(
          'flex h-[500px] items-center justify-center border-2 border-dashed border-muted-foreground bg-gray-50 transition-colors',
          isDragging && 'border-primary bg-secondary/80',
          imagePreview ? 'p-0' : 'p-10 md:p-12'
        )}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
      >
        <input
          accept="image/*"
          className="hidden"
          id="banner-upload"
          type="file"
          onChange={handleFileInput}
        />

        {imagePreview ? (
          <div className="relative size-full">
            <Image
              fill
              alt={t('banner.imageAlt')}
              className="object-cover"
              src={imagePreview}
            />
          </div>
        ) : (
          <section className="mx-auto max-w-5xl">
            <div className="flex flex-col items-center space-y-6 text-center">
              <Image
                alt={t('banner.imageAlt')}
                className="h-auto w-[250px] max-w-full"
                height={180}
                src={images.germen['banner-placeholder']}
                width={250}
              />
              <div className="space-y-2">
                <Typography
                  className="text-3xl font-semibold tracking-tight md:text-4xl"
                  variant="title-2"
                >
                  {t('banner.title')}
                </Typography>
                <Typography className="max-w-2xl text-muted-foreground">
                  {t('banner.description')}
                </Typography>
              </div>
              <Button asChild aria-label={t('banner.cta')}>
                <label
                  className="inline-flex cursor-pointer items-center gap-2"
                  htmlFor="banner-upload"
                >
                  <Icon className="size-4 shrink-0" name="plus" />
                  {t('banner.cta')}
                </label>
              </Button>
            </div>
          </section>
        )}
      </div>
    </div>
  );
};
